import api from '../utils/axiosConfig';
import {
  AutomationPlaybookPatchProposalType,
  AutomationPlaybookRunType,
  AutomationPlaybookStepDef,
  AutomationPlaybookType,
} from '../types';

export interface AutomationPlaybooksResponse {
  playbooks: AutomationPlaybookType[];
  count: number;
}

export interface AutomationPlaybookRunsResponse {
  runs: AutomationPlaybookRunType[];
  count: number;
}

export interface AutomationPlaybookPatchProposalsResponse {
  proposals: AutomationPlaybookPatchProposalType[];
  count: number;
}

export class AutomationPlaybooksService {
  static async list(clientId?: string): Promise<AutomationPlaybooksResponse> {
    try {
      const response = await api.get('/automation-playbooks', { params: clientId ? { client_id: clientId } : {} });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch automation playbooks');
    }
  }

  static async get(playbookId: string): Promise<AutomationPlaybookType> {
    try {
      const response = await api.get(`/automation-playbooks/${playbookId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch automation playbook');
    }
  }

  static async create(data: {
    name: string;
    version?: string;
    description?: string;
    client_id?: string;
    steps: AutomationPlaybookStepDef[];
  }): Promise<AutomationPlaybookType> {
    try {
      const response = await api.post('/automation-playbooks', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to create automation playbook');
    }
  }

  static async update(
    playbookId: string,
    data: Partial<{ name: string; version: string; description: string; is_active: boolean; steps: AutomationPlaybookStepDef[] }>,
  ): Promise<AutomationPlaybookType> {
    try {
      const response = await api.patch(`/automation-playbooks/${playbookId}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update automation playbook');
    }
  }

  static async remove(playbookId: string): Promise<void> {
    try {
      await api.delete(`/automation-playbooks/${playbookId}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to delete automation playbook');
    }
  }

  static async simulate(
    playbookId: string,
    alert: Record<string, unknown>,
    simulateFailingSteps?: string[],
  ): Promise<AutomationPlaybookRunType> {
    try {
      const response = await api.post(`/automation-playbooks/${playbookId}/simulate`, {
        alert,
        simulate_failing_steps: simulateFailingSteps,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to run simulation');
    }
  }

  static async listRuns(params?: { playbook_id?: string; verdict?: string }): Promise<AutomationPlaybookRunsResponse> {
    try {
      const response = await api.get('/automation-playbooks/runs', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch runs');
    }
  }

  static async generatePatchProposals(runId: string): Promise<AutomationPlaybookPatchProposalsResponse> {
    try {
      const response = await api.post(`/automation-playbooks/runs/${runId}/patch-proposals/generate`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to generate patch proposals');
    }
  }

  static async listPatchProposals(params?: { run_id?: string; status?: string }): Promise<AutomationPlaybookPatchProposalsResponse> {
    try {
      const response = await api.get('/automation-playbooks/patch-proposals', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch patch proposals');
    }
  }

  static async acceptPatchProposal(proposalId: string): Promise<AutomationPlaybookPatchProposalType> {
    try {
      const response = await api.post(`/automation-playbooks/patch-proposals/${proposalId}/accept`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to accept patch proposal');
    }
  }

  static async rejectPatchProposal(proposalId: string): Promise<AutomationPlaybookPatchProposalType> {
    try {
      const response = await api.post(`/automation-playbooks/patch-proposals/${proposalId}/reject`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to reject patch proposal');
    }
  }
}
